import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNav } from '../lib/nav'
import * as Sentry from '@sentry/react'
import { Page, TopBar } from '../components/Shell'
import { I } from '../components/Icons'
import { useScanner } from '../components/Scanner'
import { Sheet } from '../components/Sheet'
import { SkeletonTiles } from '../components/Skeleton'
import { useToast } from '../components/Toast'
import { Working } from '../components/StarTick'
import { useStore } from '../state/store'
import { lookupPiece, searchPieces, browsePieces } from '../lib/api'
import { fetchCached, getCached } from '../lib/cache'
import { thumb } from '../lib/img'
import { belowFloor, fmtC, lineCents, ownerLabel, type SoldInfo, type Piece } from '../lib/types'
import { useCountUp, shake } from '../lib/motion'
import { usePhone } from '../lib/device'

/** Right-hand cart, shared by scan, price and payment screens. */
/** `pulse`: the variant to flash once (a duplicate scan); the nonce makes a repeat flash again. Motion only. */
export function CartAside({ header, cta, ctaLabel, ctaOff, onLine, ctaAmount, pending = [], pulse }: { header: string; cta: () => void; ctaLabel: string; ctaOff: boolean; onLine?: (i: number) => void; ctaAmount?: string; pending?: string[]; pulse?: { id: string; n: number } | null }) {
  const { sale, setSale } = useStore()
  const remove = (i: number) => setSale((s) => ({ lines: s.lines.filter((_, j) => j !== i) }))
  const total = sale.lines.reduce((a, l) => a + lineCents(l), 0)
  const priced = sale.lines.filter((l) => lineCents(l) > 0).length
  // Motion: a line that joins the cart after this aside mounted slides in; the ones already there when a screen opens sit still.
  // The total rolls to its new number; the decision values (total, lines) are untouched, only what is drawn.
  const seen = useRef<Set<string> | null>(null)
  if (seen.current === null) seen.current = new Set(sale.lines.map((l) => l.piece.variant_id))
  const isNew = (id: string) => !seen.current!.has(id)
  useEffect(() => { sale.lines.forEach((l) => seen.current!.add(l.piece.variant_id)) })
  const totalShown = useCountUp(total)
  // Phone: the cart is a bar above the button (count, total, the newest piece); tapping it opens the list as a sheet
  const phone = usePhone()
  const [open, setOpen] = useState(false)
  const count = sale.lines.length + pending.length
  const body = (
      <div className="aside__body">
        <div className="icard-head"><b>{header}</b><span>{sale.lines.length} {sale.lines.length === 1 ? 'piece' : 'pieces'}</span></div>
        <div className="card" style={{ margin: '0 20px' }}>
          {sale.lines.map((l, i) => (
            <div key={l.piece.variant_id} className={'cartline' + (isNew(l.piece.variant_id) ? ' cartline--in' : '') + (pulse && pulse.id === l.piece.variant_id ? ' cartline--pulse' : '')} data-pulse={pulse && pulse.id === l.piece.variant_id ? pulse.n : undefined}>
              <button type="button" className="person cartline__main" onClick={() => { setOpen(false); onLine?.(i) }} disabled={!onLine}>
                <span className="person__initial" style={{ borderRadius: 10 }}>{i + 1}</span>
                <span style={{ flexGrow: 1, minWidth: 0 }}><span className="person__name cartline__title">{l.piece.title}</span><span className="person__sub">{l.piece.sku || l.piece.barcode} · list {fmtC(l.piece.list_cents)}</span></span>
                <span style={{ fontWeight: 700, color: belowFloor(l) ? '#d70015' : '#000', whiteSpace: 'nowrap' }}>{lineCents(l) > 0 ? fmtC(lineCents(l)) : 'No price'}</span>
              </button>
              <button type="button" className="cartline__x" aria-label={`Remove ${l.piece.title}`} onClick={() => remove(i)}>×</button>
            </div>
          ))}
          {/* D3: a scanned tag shows here at once while its lookup runs; it joins the sale only when the piece comes back */}
          {pending.map((c, i) => (
            <div key={`p${i}${c}`} className="cartline cartline--pending" aria-label={`Looking up ${c}`}>
              <span className="person cartline__main"><span className="skel skel--dot" style={{ borderRadius: 10 }} /><span style={{ flexGrow: 1, minWidth: 0 }}><span className="person__name">Looking up {c}</span><span className="skel skel--line" style={{ width: '55%', marginTop: 6 }} /></span></span>
            </div>
          ))}
          {sale.lines.length === 0 && pending.length === 0 && <div className="empty">Nothing scanned yet.</div>}
        </div>
        {sale.lines.length > 0 && <div className="hint" style={{ padding: '10px 24px' }}>{priced === sale.lines.length ? 'Priced at list. Tap a piece to change its price.' : `${priced} of ${sale.lines.length} priced. Tap a piece to change its price.`}</div>}
      </div>
  )
  const button = <button type="button" className="cta" disabled={ctaOff} onClick={() => { setOpen(false); cta() }}><span>{ctaLabel}</span><span>{ctaAmount ?? (total > 0 ? fmtC(Math.round(totalShown)) : '')}</span></button>
  if (!phone) return <>{body}<div className="aside__foot">{button}</div></>
  const last = sale.lines[sale.lines.length - 1]
  return (
    <>
      <div className="aside__foot">
        {count > 0 && (
          <button type="button" className={'cartbar' + (pulse ? ' cartbar--pulse' : '')} key={pulse ? `p${pulse.n}` : 'c'} onClick={() => setOpen(true)} aria-label={`${header}, ${count} ${count === 1 ? 'piece' : 'pieces'}. Show the list`}>
            <span className="cartbar__n" key={count}>{count}</span>
            <span className="cartbar__text"><b>{header} · {count} {count === 1 ? 'piece' : 'pieces'}</b><span>{pending.length ? `Looking up ${pending[pending.length - 1]}` : last ? last.piece.title : ''}</span></span>
            <span className="cartbar__up" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 15l6-6 6 6" /></svg></span>
          </button>
        )}
        {button}
      </div>
      {open && (
        <div className="scrim cartsheet" onClick={() => setOpen(false)}>
          <div className="sheet cartsheet__sheet" role="dialog" aria-label={header} onClick={(e) => e.stopPropagation()}>
            <div className="cartsheet__grab" aria-hidden="true" />
            <div className="cartsheet__head"><button type="button" className="cartsheet__done" onClick={() => setOpen(false)}>Done</button></div>
            {body}
            <div className="cartsheet__foot">{button}</div>
          </div>
        </div>
      )}
    </>
  )
}

/** The refusal sheet. Two states, from `product-lookup`: Already sold (the ledger holds a paid sale for the piece, rung here or
 *  brought in from a Shopify web or POS order) and Not for sale (the listing is not active in Shopify). An unpaid sale on the
 *  app never opens this; that is the heads-up toast. Motion: one short shake as it opens, so it reads as a refusal. */
export function SoldAlert({ piece, sold, onClose, children }: { piece: Piece; sold: SoldInfo; onClose: () => void; children?: ReactNode }) {
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => { const t = window.setTimeout(() => shake(box.current), 200); return () => window.clearTimeout(t) }, [])
  return (
    <Sheet onClose={onClose} center picker width={480} label="Piece not for sale">
      <div ref={box}>
        <div className="sheet__title">{sold.sale_no ? 'Already sold' : 'Not for sale'}</div>
        <div className="sheet__sub" style={{ marginTop: 6 }}>{piece.title}</div>
        <div className="card review-rows" style={{ background: 'var(--page)' }}>
          <div className="row"><span className="row__label">{sold.sale_no ? `Sale #${sold.sale_no}` : 'Shopify'}</span><span className="row__value">{sold.sale_no ? `${new Date(sold.at!).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · ID ${sold.by}` : sold.reason}</span></div>
        </div>
        <div className="hint" style={{ padding: '12px 4px' }}>{sold.sale_no && sold.via && sold.via !== 'counter' && sold.via !== 'app' ? `This piece already sold through Shopify${sold.order ? ` (order ${sold.order})` : ''} and is in the ledger as sale #${sold.sale_no}. It can't be rung again.`
          : sold.sale_no ? 'This piece was sold on this app. Check the rack: if the piece is really here, an owner can void the earlier sale from the Sales screen.'
          : 'Shopify has this piece as unavailable. Fix the listing in Shopify first.'}</div>
        {children}
        <button type="button" className="cta cta--center sheet__cta" onClick={onClose}>OK</button>
      </div>
    </Sheet>
  )
}

const OOS_NOTE = 'Out of stock in Shopify (marked by hand?). Added anyway: ringing it here records the sale, stock stays at 0.'

export function Sell() {
  const nav = useNav()
  const { sale, setSale, resetSale } = useStore()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const toast = useToast()
  const [sold, setSold] = useState<{ piece: Piece; sold: SoldInfo } | null>(null)
  const [pulse, setPulse] = useState<{ id: string; n: number } | null>(null)   // motion: the line a duplicate scan points at
  const [q, setQ] = useState('')
  const [results, setResults] = useState<(Piece & { sold_here: boolean; open_sale?: boolean })[] | null>(null)
  const [searching, setSearching] = useState(false)
  // The rack: newest listings first, more as you scroll (what Shopify's product list shows).
  // D1: the rack's first page comes up at once from the read cache and is re-read behind it
  const [rack, setRack] = useState<RackPiece[]>(() => getCached<RackPage>('rack')?.pieces ?? [])
  const [rackCursor, setRackCursor] = useState<string | null>(() => getCached<RackPage>('rack')?.cursor ?? null)
  const [rackBusy, setRackBusy] = useState(false)
  const [rackDone, setRackDone] = useState(false)
  const rackEnd = useRef<HTMLDivElement>(null)
  const loadRack = useCallback(async (cursor: string | null) => {
    if (rackBusy) return
    setRackBusy(true)
    try { const r = cursor ? await browsePieces(cursor) : await fetchCached<RackPage>('rack', () => browsePieces(null)); setRack((prev) => cursor ? [...prev, ...r.pieces] : r.pieces); setRackCursor(r.cursor); setRackDone(!r.cursor) }
    catch { setRackDone(true) } finally { setRackBusy(false) }
  }, [rackBusy])
  useEffect(() => { loadRack(null) }, [])  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = rackEnd.current; if (!el) return
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting && !rackDone && !rackBusy && rackCursor) loadRack(rackCursor) }, { rootMargin: '200px' })
    io.observe(el); return () => io.disconnect()
  }, [rackCursor, rackDone, rackBusy, loadRack])
  const seq = useRef(0)
  useEffect(() => {
    const t = q.trim()
    if (t.length < 2) { setResults(null); return }
    const my = ++seq.current
    setSearching(true)
    const h = setTimeout(() => {
      searchPieces(t).then((r) => { if (my === seq.current) { setResults(r.pieces); setSearching(false) } }).catch(() => { if (my === seq.current) { setResults([]); setSearching(false) } })
    }, 350)
    return () => clearTimeout(h)
  }, [q])
  // Search and rack results are thin (no Consignify owner, floor or intake terms); adding one runs the same full lookup a scan does.
  const addPiece = async (p: Piece) => {
    if (sale.lines.some((l) => l.piece.variant_id === p.variant_id)) { toast('That piece is already in this sale.'); setPulse((x) => ({ id: p.variant_id, n: (x?.n ?? 0) + 1 })); return }
    setBusy(true); setMsg('')
    try {
      const r = await lookupPiece(p.barcode || p.sku)
      if (r.sold) { setSold({ piece: r.piece, sold: r.sold }); return }
      const full = r.piece.variant_id === p.variant_id ? r.piece : p
      setSale((s) => ({ lines: [...s.lines, { piece: full, price: String(Math.round(full.list_cents / 100)) }] }))
      if (r.out_of_stock) toast(OOS_NOTE, { ms: 5000 })
      else if (r.open_sale) toast(`Heads up: this piece is also on unpaid sale #${r.open_sale.sale_no}. Cancel that one in Sales if it fell through.`, { ms: 5000 })
      setQ(''); setResults(null)
    } catch {
      setSale((s) => ({ lines: [...s.lines, { piece: p, price: String(Math.round(p.list_cents / 100)) }] }))
      setQ(''); setResults(null)
    } finally { setBusy(false) }
  }

  // D3: every scan puts a placeholder line in the cart at once; lookups run one after another, in scan order
  const [pending, setPending] = useState<{ id: number; code: string }[]>([])
  const queue = useRef<Promise<void>>(Promise.resolve())
  const seqId = useRef(0)
  const linesRef = useRef(sale.lines); linesRef.current = sale.lines
  const lookupOne = useRef<(c: string) => Promise<void>>(async () => {})
  lookupOne.current = async (c: string) => {
    try {
      const r = await Sentry.startSpan({ name: 'counter.scan', op: 'counter.scan' }, () => lookupPiece(c))
      if (r.sold) { setSold({ piece: r.piece, sold: r.sold }); return }
      const dup = linesRef.current.some((l) => l.piece.variant_id === r.piece.variant_id)
      setSale((s) => s.lines.some((l) => l.piece.variant_id === r.piece.variant_id) ? {} : { lines: [...s.lines, { piece: r.piece, price: String(Math.round(r.piece.list_cents / 100)) }] })
      if (dup) { toast('That piece is already in this sale.'); setPulse((x) => ({ id: r.piece.variant_id, n: (x?.n ?? 0) + 1 })) }
      else if (r.out_of_stock) toast(OOS_NOTE, { ms: 5000 })
      else if (r.same_tag?.length) toast(`Tag ${c} is on more than one listing. Added ${r.piece.title} (in stock). Also carries it: ${r.same_tag.join('; ')}. If that's the wrong piece, remove it and search by name.`)
      else if (r.open_sale) toast(`Heads up: this piece is also on unpaid sale #${r.open_sale.sale_no} (${r.open_sale.status === 'awaiting_card' ? 'card' : 'payment'} pending, ID ${r.open_sale.by}). Cancel that one in Sales if it fell through.`, { ms: 5000 })
    } catch (e) {
      const m = (e as Error).message
      if (m.includes('No piece')) toast(`No piece with tag “${c}”. Try the SKU.`, { kind: 'bad' })
      else { setMsg(m); Sentry.captureException(e) }
    }
  }
  const addRef = useRef(addPiece); addRef.current = addPiece
  const onAdd = useCallback((p: Piece) => { void addRef.current(p) }, [])
  const scan = useCallback((raw: string) => {
    const c = raw.trim(); if (!c) return
    setMsg(''); setCode('')
    const id = ++seqId.current
    setPending((p) => [...p, { id, code: c }])
    queue.current = queue.current.then(() => lookupOne.current(c)).finally(() => setPending((p) => p.filter((x) => x.id !== id)))
  }, [])
  useScanner(scan, !sold)

  const n = sale.lines.length
  return (
    <Page
      bar={<TopBar back="Home" backTo="/" title="New sale" caption={n ? `${n} scanned` : ''} />}
      aside={<CartAside pulse={pulse} header="This sale" cta={() => nav('/sell/price')} ctaLabel={pending.length ? 'Looking up…' : n ? 'Check prices' : 'Scan to start'} ctaOff={n === 0 || pending.length > 0} onLine={(i) => nav('/sell/price', { state: { step: i } })} pending={pending.map((x) => x.code)} />}
    >
      <div className="scanpad">
        <div className="scanpad__icon">{I.sell(40)}</div>
        <div className="scanpad__title">{n === 0 ? 'Scan the first tag' : n === 1 ? 'One piece in. Scan another, or price it.' : `${n} pieces in. Scan another, or price them.`}</div>
        <div className="scanpad__sub">Scan every piece in this sale first. You'll price them one at a time next.</div>
        <form className="scanpad__form" onSubmit={(e) => { e.preventDefault(); scan(code) }}>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Type the barcode or SKU" inputMode="text" autoCapitalize="characters" autoCorrect="off" />
          <button type="submit" className="seg__btn" disabled={busy || !code.trim()}>{busy ? <Working>Looking</Working> : 'Add'}</button>
        </form>
        {msg && <div className="banner" style={{ background: '#d70015', marginTop: 12 }}>{msg}</div>}
        <div className="search searchbox" style={{ marginTop: 18 }}>{I.search()}<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Or search by name, brand or SKU" autoCorrect="off" /></div>
        {results && (
          <div className="card results">
            {results.map((p) => (
              <button key={p.variant_id} type="button" className="person" disabled={p.sold_here} onClick={() => addPiece(p)} style={{ opacity: p.sold_here ? 0.45 : p.available <= 0 ? 0.7 : 1 }}>
                <span className="thumb">{p.image_url ? <img src={thumb(p.image_url, 200)} alt="" decoding="async" onError={(e) => { if (p.image_url && e.currentTarget.src !== p.image_url) e.currentTarget.src = p.image_url }} /> : null}</span>
                <span style={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}><span className="person__name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</span><span className="person__sub">{p.sku || p.barcode} · {fmtC(p.list_cents)}{p.sold_here ? ' · sold' : p.available <= 0 ? ' · out of stock' : p.open_sale ? ' · on an unpaid sale' : ''}</span></span>
                <span className="hint">{p.owner ? ownerLabel(p.owner, null, p.consignor?.name) : ''}</span>
              </button>
            ))}
            {results.length === 0 && !searching && <div className="empty">Nothing matches “{q}”.</div>}
            {searching && <div className="empty">Searching…</div>}
          </div>
        )}
        {n > 0 && <button type="button" className="topbar__back" style={{ marginTop: 24, justifyContent: 'center' }} onClick={() => { resetSale(); setMsg('') }}>Start over</button>}
        {!results && (
          <div className="rack">
            <div className="section-title" style={{ padding: '18px 4px 8px' }}>On the rack · newest first</div>
            {rack.length === 0 && !rackDone && <SkeletonTiles n={10} />}
            <div className="rack__grid">
              {rack.map((p) => <RackTile key={p.variant_id} p={p} inSale={sale.lines.some((l) => l.piece.variant_id === p.variant_id)} onAdd={onAdd} />)}
            </div>
            <div ref={rackEnd} className="empty" style={{ padding: 16 }}>{rackBusy ? <Working>Loading</Working> : rackDone ? (rack.length ? 'That’s everything in stock.' : 'Nothing in stock.') : ''}</div>
          </div>
        )}
      </div>
      {sold && <SoldAlert piece={sold.piece} sold={sold.sold} onClose={() => setSold(null)} />}
    </Page>
  )
}

type RackPiece = Piece & { sold_here: boolean; open_sale?: boolean }
type RackPage = { pieces: RackPiece[]; cursor: string | null }

/** One rack tile. Memoised (D6), so typing in the search box does not redraw every tile. */
const RackTile = memo(function RackTile({ p, inSale, onAdd }: { p: RackPiece; inSale: boolean; onAdd: (p: Piece) => void }) {
  return (
    <button type="button" className="rack__item" disabled={p.available <= 0 || inSale} onClick={() => onAdd(p)}>
      <span className="rack__img">{p.image_url ? <img src={thumb(p.image_url, 400)} alt="" loading="lazy" decoding="async" onError={(e) => { if (p.image_url && e.currentTarget.src !== p.image_url) e.currentTarget.src = p.image_url }} /> : null}</span>
      <span className="rack__brand">{p.brand}</span>
      <span className="rack__title">{p.title}</span>
      <span className="rack__price">{fmtC(p.list_cents)}{p.open_sale ? ' · on an unpaid sale' : ''}</span>
    </button>
  )
})

export function PriceHint({ l }: { l: { piece: Piece; price: string } }) {
  const c = lineCents(l)
  if (!c) return <span className="hint">Enter the price the customer is paying</span>
  const off = l.piece.list_cents > 0 ? Math.round(100 * (1 - c / l.piece.list_cents)) : 0
  const bf = belowFloor(l)
  return <span className={'hint' + (bf ? ' hint--bad' : '')}>{off > 0 ? `${off}% off list · ` : off < 0 ? 'Above list · ' : 'At list price · '}{l.piece.floor_cents == null ? 'No floor set' : bf ? 'Below floor, needs an owner' : 'Within range'}</span>
}

export { ownerLabel }
